import React from 'react';
import Wahanda from 'common/wahanda';
import moment from 'common/moment';
import { Notice } from 'components/common/Notice';
import Dialog from 'components/common/react-dialog';
import { Checkbox } from 'components/common/Checkbox';
import Loader from 'components/common/Loader';
import { DatePicker } from 'components/common/DatePicker';
import { DialogFooter, DialogFooterButton } from 'components/common/DialogFooter';
import {
  Employee,
  SetEmployeeOpenDayPayload,
  ShiftCustomTypes,
  DateRangeObject,
} from '../utils/types';
import { dateRange, isBetweenVisibleRange } from '../utils/helpers';
import { MAX_DAYS_RANGE } from '../utils/constants';
import { ShiftsAnalytics } from '../tracking';
import { ShiftModalHeader } from '../ShiftModalHeader';
import style from './ShiftRemoveCustomModal.scss';

interface Props {
  dateFrom: moment.Moment;
  onClose: () => void;
  trackingCategory?: string | null;
  // redux
  actions: {
    setEmployeesOpenDay: (param: SetEmployeeOpenDayPayload) => void;
  };
  employee: Employee;
  datePickerRange: DateRangeObject;
  isSavingShift: boolean;
}

export const ShiftRemoveCustomModal = ({
  actions,
  employee,
  datePickerRange,
  dateFrom,
  isSavingShift,
  onClose,
  trackingCategory,
}: Props) => {
  const title = Wahanda.lang.settings.shifts.modalRemoveCustom.title;
  const [startDate, setStartDate] = React.useState(dateFrom);
  const [endDate, setEndDate] = React.useState(dateFrom);
  const [checked, setChecked] = React.useState(false);
  const { employeeId, employeeImage, employeeName } = employee;
  const observer = React.useRef(false);
  const selectedDaysCount = endDate.diff(startDate, 'days') + 1;
  const isMaxTimeOffSelected = selectedDaysCount >= MAX_DAYS_RANGE;
  const isSubmitDisabled = selectedDaysCount > MAX_DAYS_RANGE;

  const onDatePickerChange = (start, end) => {
    if (startDate !== moment(start) || endDate !== moment(end)) {
      setStartDate(start);
      setEndDate(end);
    }
  };
  const handleSubmit = () => {
    const isBetween = isBetweenVisibleRange({
      datePickerRange,
      startDate,
      endDate,
    });
    const dates = dateRange({
      dateFrom: startDate.formatApiDateString(),
      dateTo: endDate.formatApiDateString(),
    });
    const types = !checked ? [ShiftCustomTypes.CUSTOM_DAY_OFF] : undefined;
    const data = {
      employeeId,
      types,
      dates,
      dateFrom: isBetween ? datePickerRange.dateFrom : undefined,
      dateTo: isBetween ? datePickerRange.dateTo : undefined,
    };
    actions.setEmployeesOpenDay(data);
    ShiftsAnalytics.trackShiftRemoveCustomModalSubmit({
      category: trackingCategory,
      property: types,
      value: dates.length,
    });
  };
  const handleClose = (event) => {
    if (event) {
      ShiftsAnalytics.trackShiftRemoveCustomModalClose({
        category: trackingCategory,
      });
    }
    onClose();
  };
  const handleCheckboxChange = () => {
    setChecked(!checked);
  };
  const maxTimeOffDaysMessage = Wahanda.Template.render(
    Wahanda.lang.settings.shifts.modalRemoveCustom.limitInfo,
    { count: MAX_DAYS_RANGE },
  );

  React.useEffect(() => {
    if (observer.current && !isSavingShift) {
      handleClose(false);
    } else {
      observer.current = true;
    }
  }, [isSavingShift]);
  React.useEffect(() => {
    ShiftsAnalytics.trackShiftRemoveCustomModalView({
      category: trackingCategory,
    });
  }, []);
  return (
    <Dialog
      dataTest="shift-remove-custom-modal"
      width={400}
      title={title}
      onClose={handleClose}
      noContentPadding
      noOverflow
    >
      <div className={style.container}>
        <ShiftModalHeader image={employeeImage} title={employeeName} />
        <div className={style.dates}>
          <div className={style.descContainer}>
            <Notice type="info" message={Wahanda.lang.settings.shifts.modalRemoveCustom.desc} />
          </div>
          <DatePicker
            labelStartDate={Wahanda.lang.settings.shifts.timeSlotFrom}
            labelEndDate={Wahanda.lang.settings.shifts.timeSlotTo}
            initialStartDate={dateFrom}
            initialEndDate={dateFrom}
            maxRangeDays={MAX_DAYS_RANGE}
            onChange={onDatePickerChange}
            uniqueId="shifts-remove-custom"
            readOnly
          />
        </div>

        {isMaxTimeOffSelected && (
          <div className={style.attentionContainer}>
            <Notice type="info" message={maxTimeOffDaysMessage} />
          </div>
        )}
        <div className={style.checkboxWrapper}>
          <Checkbox
            name="shifts-remove-custom-all-checkbox"
            dataTest="shifts-remove-custom-all-checkbox"
            label={Wahanda.lang.settings.shifts.modalRemoveCustom.removeAll}
            onChange={handleCheckboxChange}
            checked={checked}
          />
          <div className={style.checkboxNote}>
            {Wahanda.lang.settings.shifts.modalRemoveCustom.removeAllNote}
          </div>
        </div>
        {isSavingShift && <Loader positionAbsolute />}
      </div>
      <DialogFooter>
        <DialogFooterButton
          title={Wahanda.lang.shared.buttons.cancel}
          type="secondary"
          onClick={handleClose}
        />
        <DialogFooterButton
          dataTest="remove-custom-modal-button-save"
          title={Wahanda.lang.shared.buttons.save}
          onClick={handleSubmit}
          disabled={isSubmitDisabled}
        />
      </DialogFooter>
    </Dialog>
  );
};
